import { useEffect, useState, useCallback, useRef } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import {
  ButtonPseudo,
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppDispatch } from '@store';
import DocumentCreate from '../../components/bank-and-psp/create-document-form';
import {
  useConfigurationUploadMutation,
  useAddPartnerDocumentsMutation,
  useAddAccountDocumentsMutation,
} from './store/services';
import { UploadResponse, CreateDocumentTypes } from './types';

import { BankRoutes } from '../../routes';

const DocumentCreateHOC = ({ accountPage }: CreateDocumentTypes) => {
  const dropZoneInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; id: string }[]
  >([]);
  const [uploadedFilesErrors, setUploadedFilesErrors] = useState<string[]>([]);
  const [globalError, setGlobalError] = useState('');

  const { bankId, accountId } = useParams();

  const { search } = useLocation();

  const [upload, { isLoading: fileUploadInProgress }] =
    useConfigurationUploadMutation();
  const [
    addPartnerDocuments,
    { isLoading: requestInProgress, isSuccess: onAddSuccess },
  ] = useAddPartnerDocumentsMutation();
  const [
    addAccountDocuments,
    { isLoading: accountRequestInProgress, isSuccess: onAccountAddSuccess },
  ] = useAddAccountDocumentsMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onSubmitDocuments = async () => {
    if (accountPage) {
      const response = await Promise.all(
        uploadedFiles.map(
          (item) =>
            addAccountDocuments({
              id: accountId as string,
              body: { uploadId: item.id, name: item.name },
            }) as { error?: ResponseError },
        ),
      );

      if (response?.find((item) => item?.error)) {
        responseErrorsResolver({
          errors: response?.find((item) => item?.error)?.error as ResponseError,
          setError: () => {},
          setGlobalError,
        });
      }
    } else {
      const response = await Promise.all(
        uploadedFiles.map(
          (item) =>
            addPartnerDocuments({
              id: bankId as string,
              body: { uploadId: item.id, name: item.name },
            }) as { error?: ResponseError },
        ),
      );

      if (response?.find((item) => item?.error)) {
        responseErrorsResolver({
          errors: response?.find((item) => item?.error)?.error as ResponseError,
          setError: () => {},
          setGlobalError,
        });
      }
    }
  };

  const onUpload = async (files: FileList) => {
    setUploadedFilesErrors([]);
    const nextUploadedFilesErrors: string[] = [];

    const responseFiles = await Promise.all(
      Object.keys(files).map(async (file) => {
        const response = (await upload(files[file as any])) as unknown as {
          data?: UploadResponse[];
          error?: {
            originalStatus: number;
          };
        };

        let nextFile;

        if (response && response.data) {
          nextFile = { ...response.data[0] };

          let fileName = files[file as any]?.name;

          if (
            response.data[0] &&
            response.data[0]?.mimeType?.includes('image')
          ) {
            fileName = `${files[file as any]?.name.slice(0, files[file as any]?.name.lastIndexOf('.'))}.jpg`;
          }

          nextFile.name = fileName;
        } else if (response.error && response.error?.originalStatus === 413) {
          nextUploadedFilesErrors.push(
            `${files[file as any].name} size is too large`,
          );
        }

        return nextFile;
      }),
    );

    setUploadedFilesErrors(nextUploadedFilesErrors);

    if (responseFiles && responseFiles.length) {
      const nextUploadedFiles = ([] as { name: string; id: string }[]).concat(
        uploadedFiles,
      );

      responseFiles.forEach((item) => {
        if (item && item.name && item.id) {
          nextUploadedFiles.push({ name: item.name, id: item.id });
        }
      });

      setUploadedFiles(nextUploadedFiles);
    }

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }
    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }
  };

  const onDelete = (id: string) => {
    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    let nextUploadedFiles = ([] as { name: string; id: string }[]).concat(
      uploadedFiles,
    );

    if (nextUploadedFiles.find((item) => item.id === id)) {
      nextUploadedFiles = nextUploadedFiles.filter((item) => item.id !== id);

      setUploadedFiles(nextUploadedFiles);
    }
  };

  const handleClose = useCallback(() => {
    setGlobalError('');
    setPanelIsOpen(false);
    setUploadedFilesErrors([]);
    setUploadedFiles([]);

    let link = '';

    if (accountPage && accountId) {
      link = generatePath(BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE, {
        accountId,
      });
    } else {
      link = generatePath(BankRoutes.BANK_AND_PSP_GENERAL, {
        bankId: bankId as string,
      });
    }

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (onAddSuccess || onAccountAddSuccess) {
      handleClose();
    }
  }, [onAddSuccess, onAccountAddSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, [dispatch]);

  return (
    <SidePanel
      type={SidePanelType.Cards}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      errorSidepanel={globalError}
      title="Add document"
      sidepanelExpandedKey="bank-create-document"
      footer={
        <>
          <ButtonPrimary
            text={
              requestInProgress || accountRequestInProgress
                ? 'Loading...'
                : 'Add document'
            }
            disabled={Boolean(!uploadedFiles.length)}
            onClick={onSubmitDocuments}
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
    >
      <DocumentCreate
        onUpload={onUpload}
        fileUploadInProgress={fileUploadInProgress}
        uploadInputRef={uploadInputRef}
        dropZoneInputRef={dropZoneInputRef}
        onDelete={onDelete}
        uploadedFiles={uploadedFiles}
        uploadedFilesErrors={uploadedFilesErrors}
      />
    </SidePanel>
  );
};

export default DocumentCreateHOC;
